import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { ILegalRequirementTemplateFullObject } from '../Interfaces/ILegalRequirementTemplate';

import { Edit } from '@mui/icons-material';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { useSelector } from 'react-redux';
import { DocumentOverview } from '../Document/DocumentOverview';
import { ICourtCourtRole } from '../Interfaces/IAemter';
import { IDocument } from '../Interfaces/IDocument';
import { RequirementResponsibilityCard } from '../LegalTransaction/LegalPhase/Edit/RequirementResponsibilityCard';
import { CollapsibleSection } from '../core/CollapsibleSection';
import { ILegalPhaseTemplateFullObject } from '../Interfaces/ILegalPhaseTemplate';
import { ILegalRequirementTemplateDeadline } from '../Interfaces/ILegalRequirementTemplate_deadlines';
import { TextModuleSelector } from '../TextModule/TextModuleSelector';
import { CustomEditor } from '../core/CustomEditor';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { getEmptyLegalRequirementTemplateFullObject } from '../core/generic_helper';
import { useGetLegalRequirementTemplateById } from '../hooks/reactQuerys/LegalRequirementQueries';
import { uploadFetch, useFetch } from '../hooks/useFetch';
import { State } from '../redux/mainReducer';
import { AddPrerequisiteDeadline } from './AddPrerequisiteDeadline';
import { LegalRequirementDeadlineTable } from './LegalRequirementDeadlineTable';
import { showConfirmDialogComponent } from '../core/ConfirmDialog';
import { CustomeTextField } from '../generic/CustomeTextField';

interface IProps {
  currentRequirementTemplateid: number;
  setCurrentRequirementTemplateid: Function;
  legalPhaseTemplateArray?: ILegalPhaseTemplateFullObject[];
  setWasSaved: Function;
  title: string;
  setLegalRequirementTemplateWasSuccessfullySaved: Function;
}

export const LegalRequirementTemplateEdit: React.FC<IProps> = ({
  currentRequirementTemplateid,
  setCurrentRequirementTemplateid,
  legalPhaseTemplateArray,
  setWasSaved,
  setLegalRequirementTemplateWasSuccessfullySaved,
  title,
}) => {
  const [
    requirementArray,
    setrequirementArray,
    wasSuccessfullyRequirementArray,
  ] = useFetch<ILegalRequirementTemplateFullObject[]>(
    '/legalrequirementtemplate'
  );
  const {
    legalRequirementTemplate,
    isLegalRequirementTemplateFetching,
    refetchLegalRequirementTemplate,
  } = useGetLegalRequirementTemplateById(
    currentRequirementTemplateid > 0 ? currentRequirementTemplateid : 0
  );
  //
  const [editCurrentRequirement, setEditCurrentRequirement] =
    useState<ILegalRequirementTemplateFullObject>(
      getEmptyLegalRequirementTemplateFullObject()
    );
  const [currentDocumentArray, setCurrentDocumentArray] = useState<IDocument[]>(
    []
  );
  //
  useEffect(() => {
    if (legalRequirementTemplate !== undefined) {
      if (currentRequirementTemplateid >= 0) {
        setEditCurrentRequirement(legalRequirementTemplate);
        setCurrentDocumentArray(legalRequirementTemplate.DocumentArray);
      } else if (currentRequirementTemplateid < 0) {
        setEditCurrentRequirement(getEmptyLegalRequirementTemplateFullObject());
        setCurrentDocumentArray([]);
      }
    }
  }, [legalRequirementTemplate]);
  // Redux
  const legalRequirementStateArray = useSelector(
    (state: State) =>
      state.legalRequirementStateArray.storeLegalRequirementState
  );

  const [courtcourtRoleArray, , wasSuccessfullyLoadCourt] =
    useFetch<ICourtCourtRole[]>('/courtcourtrole');

  const [addPrerequisteDeadlineOpen, setAddPrerequisteDeadlineOpen] =
    useState(false);
  //
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);

  const wrappeHandleSaveRequirement = (
    localRequirement: ILegalRequirementTemplateFullObject
  ) => {
    if (editCurrentRequirement !== null && requirementArray !== undefined) {
      if (editCurrentRequirement?.idLegalRequirementTemplate < 0) {
        setrequirementArray([...requirementArray, localRequirement]);
        setLegalRequirementTemplateWasSuccessfullySaved(true);
      } else {
        setrequirementArray([
          ...requirementArray.map(x =>
            x.idLegalRequirementTemplate ===
            localRequirement.idLegalRequirementTemplate
              ? localRequirement
              : x
          ),
        ]);
      }
      setWasSaved(true);
      refetchLegalRequirementTemplate();
      setIsLoading(false);
      setCurrentRequirementTemplateid(null);
    }
  };

  const handleSaveRequirement = async () => {
    const confirmed = await showConfirmDialogComponent(
      'Warnung',
      'Wenn Sie Ihre Änderungen speichern, bearbeiten Sie eine Vorlage. Die Änderungen werden künftig auf alle entsprechenden Maßnahmen angewendet. Bereits bestehende Maßnahmen sind davon nicht betroffen.',
      'Speichern',
      'Abbrechen',
      theme
    );

    if (!confirmed) {
      return;
    }

    if (editCurrentRequirement !== null) {
      setIsLoading(true); // Ladezustand aktivieren
      const uploadObject = {
        ...editCurrentRequirement,
        DocumentArray: currentDocumentArray,
        canUploadFilesClient: Boolean(
          editCurrentRequirement.canUploadFilesClient
        ),
        canUploadFilesEmployees: Boolean(
          editCurrentRequirement.canUploadFilesEmployees
        ),
        hasError: Boolean(editCurrentRequirement.hasError),
        hasErrorFreeText: Boolean(editCurrentRequirement.hasErrorFreeText),
        isTemplate: Boolean(editCurrentRequirement.isTemplate),
        sendMail: Boolean(editCurrentRequirement.sendMail),
      } as ILegalRequirementTemplateFullObject;

      try {
        await uploadFetch(
          '/legalrequirementtemplate/fullObject',
          Number(editCurrentRequirement?.idLegalRequirementTemplate) < 0,
          uploadObject,
          wrappeHandleSaveRequirement,
          undefined, // setWasSuccessfullyUploaded
          undefined, // wasSaved
          undefined // setIsLoading
        );
      } catch (error) {
      } finally {
      }
    }
  };

  const handleChangePrerequisteDeadline = useCallback(
    (changedPrequiste: ILegalRequirementTemplateDeadline) => {
      const newArray = editCurrentRequirement.DeadlineArray
        ? editCurrentRequirement.DeadlineArray
        : [];

      if (changedPrequiste.idDeadline === -1) {
        changedPrequiste.idDeadline = Math.floor(Math.random() * 1000000);
        newArray.push(changedPrequiste);
      } else {
        const index = newArray.findIndex(
          x => x.idDeadline === changedPrequiste.idDeadline
        );
        if (index === -1) {
          newArray.push(changedPrequiste);
        } else {
          newArray[index] = changedPrequiste;
        }
      }
      setEditCurrentRequirement({
        ...editCurrentRequirement,
        DeadlineArray: newArray,
      });
    },
    [
      currentRequirementTemplateid,
      setCurrentRequirementTemplateid,
      legalPhaseTemplateArray,
    ]
  );

  const handleDeleteDeadline = useCallback(
    (index: number) => {
      const newArray = editCurrentRequirement.DeadlineArray
        ? editCurrentRequirement.DeadlineArray
        : [];
      newArray.splice(index, 1);
      setEditCurrentRequirement({
        ...editCurrentRequirement,
        DeadlineArray: newArray,
      });
    },
    [
      currentRequirementTemplateid,
      setCurrentRequirementTemplateid,
      legalPhaseTemplateArray,
    ]
  );

  if (
    !wasSuccessfullyLoadCourt ||
    !wasSuccessfullyLoadCourt ||
    !wasSuccessfullyRequirementArray
  ) {
    return <div>Fehler beim Laden der Daten</div>;
  } else if (
    courtcourtRoleArray === undefined ||
    requirementArray === undefined ||
    (legalRequirementTemplate === undefined &&
      currentRequirementTemplateid > 0 &&
      isLegalRequirementTemplateFetching)
  ) {
    return (
      <>
        <LoadingSpinner />
      </>
    );
  }
  return (
    <>
      <DialogTitle
        variant='h5'
        sx={{
          borderBottom: '1px solid #ccc', // Optional: Eine Trennlinie für visuelle Klarheit
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ m: 2 }}>
          <Grid container spacing={2}>
            <Grid item sm={2}>
              <TextField
                label='interne Maßnahme'
                value={
                  editCurrentRequirement.isInterneLegalRequirement
                    ? 'true'
                    : 'false'
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEditCurrentRequirement({
                    ...editCurrentRequirement,
                    isInterneLegalRequirement: event.target.value === 'true',
                    sendMail:
                      event.target.value === 'true'
                        ? false
                        : editCurrentRequirement.sendMail,
                  })
                }
                size='small'
                fullWidth
                select
              >
                <MenuItem value='true'>Ja</MenuItem>
                <MenuItem value='false'>Nein</MenuItem>{' '}
              </TextField>
            </Grid>
            <Grid item sm={10}>
              <TextField
                label='Bedingung'
                value={editCurrentRequirement.LegalRequirementTemplate}
                error={editCurrentRequirement.LegalRequirementTemplate === ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEditCurrentRequirement({
                    ...editCurrentRequirement,
                    LegalRequirementTemplate: event.target.value,
                  })
                }
                size='small'
                fullWidth
              />
            </Grid>

            <Grid item sm={6}>
              <TextField
                label='Mitarbeiter kann Dateien hochladen'
                value={
                  editCurrentRequirement.canUploadFilesEmployees
                    ? 'true'
                    : 'false'
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setEditCurrentRequirement({
                    ...editCurrentRequirement,
                    canUploadFilesEmployees: event.target.value === 'true',
                    DocumentArray:
                      event.target.value === 'false'
                        ? []
                        : editCurrentRequirement.DocumentArray,
                  });
                  setCurrentDocumentArray(
                    event.target.value === 'false' ? [] : currentDocumentArray
                  );
                }}
                size='small'
                fullWidth
                select
              >
                <MenuItem value='true'>Ja</MenuItem>
                <MenuItem value='false'>Nein</MenuItem>
              </TextField>
            </Grid>
            <Grid item sm={6}>
              <TextField
                label='Mandant kann Dateien hochladen'
                value={
                  editCurrentRequirement.canUploadFilesClient ? 'true' : 'false'
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEditCurrentRequirement({
                    ...editCurrentRequirement,
                    canUploadFilesClient: event.target.value === 'true',
                  })
                }
                size='small'
                fullWidth
                select
              >
                <MenuItem value='true'>Ja</MenuItem>
                <MenuItem value='false'>Nein</MenuItem>
              </TextField>
            </Grid>

            <Grid item sm={5}>
              <TextField
                label='Startstatus'
                value={editCurrentRequirement.idLegalRequirementState}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEditCurrentRequirement({
                    ...editCurrentRequirement,
                    idLegalRequirementState: Number(event.target.value),
                  })
                }
                size='small'
                fullWidth
                select
              >
                {legalRequirementStateArray.map(x => (
                  <MenuItem
                    key={`idLegalRequirementState-${x.idLegalRequirementState}`}
                    value={x.idLegalRequirementState}
                  >
                    {x.LegalRequirementState}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item sm={3}>
              <TextField
                label='Vorlage im MA-Katalog'
                disabled={editCurrentRequirement.idLegalPhaseTemplate === null}
                value={editCurrentRequirement.isTemplate ? 'true' : 'false'}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEditCurrentRequirement({
                    ...editCurrentRequirement,
                    isTemplate: event.target.value === 'true',
                  })
                }
                size='small'
                fullWidth
                select
              >
                <MenuItem value='true'>Ja</MenuItem>
                <MenuItem value='false'>Nein</MenuItem>
              </TextField>
            </Grid>

            <Grid item sm={4}>
              <TextField
                label='Vorgänger (Bedingung)'
                value={
                  editCurrentRequirement.Predecessor_idLegalRequirementTemplate ===
                  undefined
                    ? ''
                    : editCurrentRequirement.Predecessor_idLegalRequirementTemplate
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEditCurrentRequirement({
                    ...editCurrentRequirement,
                    Predecessor_idLegalRequirementTemplate: Number(
                      event.target.value
                    ),
                  })
                }
                size='small'
                fullWidth
                select
              >
                {requirementArray.map(x => (
                  <MenuItem
                    key={`predecessor-idLegalRequirementTemplate-${x.idLegalRequirementTemplate}`}
                    value={x.idLegalRequirementTemplate}
                  >
                    {x.LegalRequirementTemplate}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {editCurrentRequirement.canUploadFilesEmployees && (
              <Grid mt={6} item sm={12}>
                <DocumentOverview
                  documentArray={currentDocumentArray}
                  setDocumentArray={setCurrentDocumentArray}
                  allowUpload={true}
                  allowRemove={true}
                  title={'Standarddokumente'}
                  isStandarddocument={true}
                  isTemplate={true}
                />
              </Grid>
            )}

            <Grid item sm={12}>
              <RequirementResponsibilityCard
                mode='template'
                value={editCurrentRequirement}
                onChange={next =>
                  setEditCurrentRequirement({
                    ...editCurrentRequirement,
                    ...next,
                  })
                }
              />
            </Grid>
            <Grid item sm={12}>
              <CollapsibleSection
                title='Startbedingungen'
                subtitle={
                  editCurrentRequirement?.DeadlineArray &&
                  editCurrentRequirement.DeadlineArray.length > 0
                    ? `${editCurrentRequirement.DeadlineArray.length} Bedingung(en) hinterlegt`
                    : 'Wann diese Maßnahme starten darf — optional'
                }
                icon={<PlaylistAddCheckIcon color='action' />}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} display='flex' justifyContent='flex-end'>
                    <Tooltip title='Bearbeiten Sie die Startbedingungen'>
                      <IconButton
                        size='small'
                        onClick={() => setAddPrerequisteDeadlineOpen(true)}
                      >
                        <Edit fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12}>
                    <LegalRequirementDeadlineTable
                      deadlineArray={editCurrentRequirement?.DeadlineArray}
                      setDeadline={handleChangePrerequisteDeadline}
                      deleteDeadline={handleDeleteDeadline}
                    />
                  </Grid>
                </Grid>
              </CollapsibleSection>
            </Grid>
            <Grid item sm={12}>
              <TextModuleSelector
                key={`type-legalrequirementedit`}
                floatRight
                idTextModuleCategory={4}
                txt={editCurrentRequirement?.InfoText || ''}
                setTxt={(txt: string) =>
                  setEditCurrentRequirement({
                    ...editCurrentRequirement,
                    InfoText: txt,
                  })
                }
              />
              <TextField
                label='Infotext'
                value={
                  editCurrentRequirement?.InfoText === null
                    ? ''
                    : editCurrentRequirement?.InfoText
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEditCurrentRequirement({
                    ...editCurrentRequirement,
                    InfoText:
                      event.target.value === '' ? null : event.target.value,
                  })
                }
                size='small'
                fullWidth
                multiline
                rows={3}
              />
            </Grid>

            <Grid item sm={4}>
              <TextField
                label='Auto. E-Mail versenden (wenn erfüllt)'
                value={editCurrentRequirement.sendMail ? 'true' : 'false'}
                disabled={editCurrentRequirement.isInterneLegalRequirement}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEditCurrentRequirement({
                    ...editCurrentRequirement,
                    sendMail: event.target.value === 'true',
                  })
                }
                size='small'
                fullWidth
                select
              >
                <MenuItem value='true'>Ja</MenuItem>
                <MenuItem value='false'>Nein</MenuItem>
              </TextField>
            </Grid>

            <Grid item sm={8}>
              <CustomeTextField
                label='E-Mail Betreff'
                attr='EmailSubject'
                object={editCurrentRequirement}
                setObject={setEditCurrentRequirement}
                type='string'
                size='small'
                maxLength='md'
                error={
                  editCurrentRequirement.sendMail &&
                  editCurrentRequirement.EmailSubject === ''
                }
                helperText={
                  editCurrentRequirement.sendMail &&
                  editCurrentRequirement.EmailSubject === ''
                    ? 'E-Mail Betreff ist erforderlich'
                    : undefined
                }
              />
            </Grid>

            <CustomEditor
              txt={
                editCurrentRequirement.EmailBody === null
                  ? ''
                  : editCurrentRequirement.EmailBody
              }
              setTxt={(txt: string) =>
                setEditCurrentRequirement({
                  ...editCurrentRequirement,
                  EmailBody: txt === '' ? null : txt,
                })
              }
              idTextModuleCategory={3}
              dictonaryIdsArray={[1, 2, 3]}
            />

            <Grid item sm={6}>
              <TextField
                label='Fehler möglich'
                value={editCurrentRequirement.hasError ? 'true' : 'false'}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEditCurrentRequirement({
                    ...editCurrentRequirement,
                    hasError: event.target.value === 'true',
                  })
                }
                size='small'
                fullWidth
                select
              >
                <MenuItem value='true'>Ja</MenuItem>
                <MenuItem value='false'>Nein</MenuItem>
              </TextField>
            </Grid>
            <Grid item sm={6}>
              <TextField
                label='Freitext bei Fehler erlauben'
                value={
                  editCurrentRequirement.hasErrorFreeText ? 'true' : 'false'
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEditCurrentRequirement({
                    ...editCurrentRequirement,
                    hasErrorFreeText: event.target.value === 'true',
                  })
                }
                size='small'
                fullWidth
                select
              >
                <MenuItem value='true'>Ja</MenuItem>
                <MenuItem value='false'>Nein</MenuItem>
              </TextField>
            </Grid>

            <Grid item sm={12}>
              <TextField
                label='Fehlermeldung'
                value={editCurrentRequirement.TextOfError}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEditCurrentRequirement({
                    ...editCurrentRequirement,
                    TextOfError: event.target.value,
                  })
                }
                size='small'
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
          <AddPrerequisiteDeadline
            open={addPrerequisteDeadlineOpen}
            currentLegalRequirementTemplate={editCurrentRequirement}
            legalRequirementTemplateArray={requirementArray}
            setDeadline={handleChangePrerequisteDeadline}
            onClose={() => setAddPrerequisteDeadlineOpen(false)}
            legalPhaseTemplateArray={legalPhaseTemplateArray}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant='outlined'
          onClick={() => setCurrentRequirementTemplateid(null)}
        >
          Schließen
        </Button>
        <Button
          variant='contained'
          disabled={
            isLoading ||
            editCurrentRequirement.LegalRequirementTemplate === '' ||
            (editCurrentRequirement.sendMail &&
              (editCurrentRequirement.EmailSubject === '' ||
                editCurrentRequirement.EmailSubject === null ||
                editCurrentRequirement.EmailBody === '' ||
                editCurrentRequirement.EmailBody === null))
          }
          onClick={handleSaveRequirement}
        >
          {isLoading ? (
            <Box display='flex' alignItems='center' gap={1}>
              <span>Speichern</span>
              <CircularProgress size={20} />
            </Box>
          ) : (
            'Speichern'
          )}
        </Button>
      </DialogActions>
    </>
  );
};
